import { useEffect } from 'react';
import type { RefObject } from 'react';
import type { Frame } from '../store/types';
import { compileScript } from './compileScript';
import { createCtx, type FrameCtx } from './createCtx';

type Mod = { default?: (root: HTMLElement, ctx: FrameCtx) => undefined | (() => void) };

export async function runFrameScript(opts: {
  src: string;
  root: HTMLElement;
  ctx: FrameCtx;
  importer?: (url: string) => Promise<Mod>;
}): Promise<() => void> {
  const { src, root, ctx } = opts;
  const importer = opts.importer ?? ((url: string) => import(/* @vite-ignore */ url) as Promise<Mod>);
  // Only compile a real blob when using the real importer; injected importers
  // (tests) skip blob creation entirely.
  const { url, revoke } = opts.importer ? { url: '', revoke: () => {} } : compileScript(src);
  let cleanup: (() => void) | undefined;
  try {
    const mod = await importer(url);
    cleanup = mod.default?.(root, ctx) ?? undefined;
  } catch (e) {
    console.warn('[script] run failed', e);
  }
  return () => {
    try { cleanup?.(); } catch (e) { console.warn('[script] cleanup failed', e); }
    revoke();
  };
}

function scriptOf(frame: Frame): string | undefined {
  return frame.kind === 'free' || frame.kind === 'main' ? frame.script : undefined;
}

export function useFrameScript(ref: RefObject<HTMLElement>, frame: Frame, invalidate?: () => void): void {
  const src = scriptOf(frame);
  useEffect(() => {
    if (!ref.current || !src) return;
    const ctx = createCtx({ frameId: frame.id, root: ref.current, invalidate });
    let teardown: (() => void) | undefined;
    let disposed = false;
    runFrameScript({ src, root: ref.current, ctx }).then((t) => {
      if (disposed) t(); else teardown = t;
    });
    return () => { disposed = true; teardown?.(); };
    // Re-run when the script source changes (live edit); html changes re-mount the node.
  }, [ref, frame.id, src, invalidate]);
}
